import { useEffect, useRef, useState } from "react";

/**
 * Returns a debounced copy of `value` that only updates after `delayMs` has
 * elapsed without a change.
 *
 * `onDebounced` (when provided) runs in the same tick as the debounced update,
 * so callers can batch a related side effect — e.g. resetting pagination to
 * page 1 — into the same render, avoiding a transient extra fetch.
 */
export function useDebouncedValue<T>(
  value: T,
  delayMs: number,
  onDebounced?: (value: T) => void,
): T {
  const [debounced, setDebounced] = useState(value);
  // Keep the latest callback without retriggering the timer on identity change.
  const onDebouncedRef = useRef(onDebounced);
  onDebouncedRef.current = onDebounced;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
      onDebouncedRef.current?.(value);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
